import type { CalculationResponse, ModuleResult } from '../../gen/ejot/roofing/v1/types';
import { formatCount } from '../../shared/format';

export interface ZoneFixingQuantity {
  zoneId: string;
  label: string;
  fixings: number;
}

export function zoneFixingQuantities(module: ModuleResult | undefined): ZoneFixingQuantity[] {
  return (module?.zones ?? [])
    .filter((zone) => zone.layout?.total_fixings !== undefined)
    .map((zone) => ({
      zoneId: zone.zone_id ?? '',
      label: zone.display_label ?? zone.zone_id ?? '—',
      fixings: zone.layout?.total_fixings ?? 0,
    }));
}

/** Display-only reconciliation; it never changes the authoritative backend total. */
export function reconcilesWithBackendTotal(module: ModuleResult | undefined): boolean | undefined {
  if (module?.quantities?.total_fixings === undefined) return undefined;
  const rows = zoneFixingQuantities(module);
  if (rows.length === 0) return undefined;
  return rows.reduce((sum, row) => sum + row.fixings, 0) === module.quantities.total_fixings;
}

export function ZoneQuantityBreakdown({
  result,
  module,
}: {
  result: CalculationResponse | undefined;
  module: ModuleResult | undefined;
}) {
  const rows = zoneFixingQuantities(module);
  if (rows.length === 0) return null;
  const reconciles = reconcilesWithBackendTotal(module);

  return (
    <section className="result-section" aria-label="Zone fixing quantity breakdown">
      <h3 className="result-section-title">Fixing quantities by zone</h3>
      <table className="engineering-table">
        <thead>
          <tr><th scope="col">Zone</th><th scope="col" className="numeric">Fixings</th></tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.zoneId}>
              <td>{row.label}</td>
              <td className="numeric">{formatCount(row.fixings)} pcs</td>
            </tr>
          ))}
        </tbody>
      </table>
      <dl className="definition-list">
        <dt>Backend module total</dt>
        <dd>{formatCount(module?.quantities?.total_fixings)} pcs</dd>
        {result?.project_quantities?.total_fixings !== undefined && (
          <><dt>Backend project total</dt><dd>{formatCount(result.project_quantities.total_fixings)} pcs</dd></>
        )}
        <dt>Zone reconciliation</dt>
        <dd>{reconciles === undefined ? 'Unavailable' : reconciles ? 'Matches backend total' : 'Does not match backend total'}</dd>
      </dl>
      <p className="field-note">
        The reconciliation sum is a display audit only. Backend module/project totals and BOM lines remain the authoritative quantities.
      </p>
    </section>
  );
}
